import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const AuditTimeline = ({ submissionId }) => {
  const [submission, setSubmission] = useState(null);

  useEffect(() => {
    setSubmission({ status: 'pending' });
  }, [submissionId]);

  const steps = [
    { id: 1, label: 'Payment Confirmed',  description: 'Order received and payment verified',            completed: true,  icon: 'CheckCircle2', estimatedTime: 'Completed'        },
    { id: 2, label: 'File Validation',    description: 'Checking deck format and accessibility',          completed: submission?.status !== 'pending', icon: 'FileCheck', estimatedTime: '1–2 hours' },
    { id: 3, label: 'AI Analysis',        description: 'Automated content and structure analysis',        completed: false, icon: 'Cpu',          estimatedTime: '2–4 hours'        },
    { id: 4, label: 'Expert Assignment',  description: 'Matched with specialized analyst',               completed: false, icon: 'UserCheck',    estimatedTime: '4–8 hours'        },
    { id: 5, label: 'Human Review',       description: 'Expert analysis and recommendations',             completed: false, icon: 'Users',        estimatedTime: '12–24 hours'      },
    { id: 6, label: 'Quality Assurance',  description: 'Final review and report preparation',             completed: false, icon: 'Award',        estimatedTime: '2–4 hours'        },
    { id: 7, label: 'Report Delivered',   description: 'Comprehensive audit report published',            completed: false, icon: 'Send',         estimatedTime: '24–48 hours total'},
  ];

  const completedCount = steps.filter(s => s.completed).length;

  return (
    <div style={{
      background: 'var(--card, #ffffff)',
      border: '1px solid var(--border, #e2e8f0)',
      borderRadius: 16, padding: '22px 22px 24px',
      fontFamily: 'inherit',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 22 }}>
        <Icon name="Clock" size={20} color="#004AAD" />
        <div>
          <h2 style={{
            fontFamily: "'Sansation', sans-serif",
            fontSize: 16, fontWeight: 700,
            color: 'var(--foreground, #0f172a)', margin: 0,
          }}>Audit Timeline</h2>
          <p style={{ fontFamily: 'inherit', fontSize: 11, color: 'var(--muted-foreground, #64748b)', margin: 0 }}>
            Premium analysis process
          </p>
        </div>
      </div>

      {/* Steps */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {steps.map((step, index) => {
          const isLast = index === steps.length - 1;
          return (
            <div key={step.id} style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 12, paddingBottom: isLast ? 0 : 20 }}>

              {/* Connector line */}
              {!isLast && (
                <div style={{
                  position: 'absolute', left: 19, top: 40,
                  width: 2, height: 'calc(100% - 20px)',
                  background: step.completed ? 'rgba(22,163,74,0.30)' : 'var(--border, #e2e8f0)',
                }} />
              )}

              {/* Icon circle */}
              <div style={{
                flexShrink: 0, width: 40, height: 40, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: step.completed ? 'rgba(22,163,74,0.10)' : 'var(--muted, #f8fafc)',
                border: `2px solid ${step.completed ? 'rgba(22,163,74,0.30)' : 'var(--border, #e2e8f0)'}`,
                zIndex: 1,
              }}>
                <Icon
                  name={step.icon}
                  size={17}
                  color={step.completed ? '#16a34a' : 'var(--muted-foreground, #94a3b8)'}
                />
              </div>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0, paddingTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 3 }}>
                  <h4 style={{
                    fontFamily: 'inherit', fontSize: 13, fontWeight: 600, margin: 0,
                    color: step.completed ? 'var(--foreground, #0f172a)' : 'var(--muted-foreground, #64748b)',
                  }}>
                    {step.label}
                  </h4>
                  {step.completed ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontSize: 11, fontWeight: 700, color: '#16a34a',
                      whiteSpace: 'nowrap', fontFamily: 'inherit',
                    }}>
                      <Icon name="Check" size={11} color="#16a34a" /> Done
                    </span>
                  ) : (
                    <span style={{ fontSize: 11, color: 'var(--muted-foreground, #94a3b8)', whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
                      {step.estimatedTime}
                    </span>
                  )}
                </div>
                <p style={{ fontFamily: 'inherit', fontSize: 12, color: 'var(--muted-foreground, #64748b)', margin: 0, lineHeight: 1.5 }}>
                  {step.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary stats */}
      <div style={{
        marginTop: 20, paddingTop: 20,
        borderTop: '1px solid var(--border, #e2e8f0)',
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, textAlign: 'center',
      }}>
        <div>
          <p style={{
            fontFamily: "'Sansation', sans-serif",
            fontSize: 22, fontWeight: 700,
            background: 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            margin: '0 0 3px',
          }}>{completedCount}/{steps.length}</p>
          <p style={{ fontFamily: 'inherit', fontSize: 11, color: 'var(--muted-foreground, #64748b)', margin: 0 }}>Steps Completed</p>
        </div>
        <div>
          <p style={{
            fontFamily: "'Sansation', sans-serif",
            fontSize: 22, fontWeight: 700,
            color: 'var(--foreground, #0f172a)', margin: '0 0 3px',
          }}>24–48h</p>
          <p style={{ fontFamily: 'inherit', fontSize: 11, color: 'var(--muted-foreground, #64748b)', margin: 0 }}>Est. Completion</p>
        </div>
      </div>

      {/* Priority badge */}
      <div style={{
        marginTop: 16, padding: '10px 14px',
        background: 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)',
        borderRadius: 10,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <Icon name="Zap" size={15} color="#ffffff" style={{ flexShrink: 0 }} />
        <p style={{ fontFamily: 'inherit', fontSize: 12, fontWeight: 600, color: '#ffffff', margin: 0 }}>
          Priority Processing — Your audit is in the fast lane
        </p>
      </div>
    </div>
  );
};

export default AuditTimeline;